import * as serializer from 'serializr';
import * as halfred from 'halfred';
import {
   hasMobXSerializeDecorators,
   optionalMetadataKey,
   validateOptionalMobXSerializable
} from '../validation/Validator';
import { LinkModel } from "./LinkModel";

type Map<T> = { [key: string]: T }
type Clazz<T> = { new(...args: any[]): T }
type MetaData<T> = { key: string, clazz: Clazz<T> | typeof LinkModel }
type DecoratedHalObject<T> = { __embeddeds__?: Map<MetaData<T>> | undefined, __links__?: Map<MetaData<LinkModel>> | undefined }

/**
 * The embedded class decorator.  See [[HalIO]].
 *
 * @param {string} key The rel key such as "user:children"
 * @param {Class<T>} clazz The resource data model type to return in the array result
 * @returns {(target: object, decoratedPropertyName: (string | symbol)) => void}
 */
export const embedded = <T>(key: string, clazz: Clazz<T>) => {
   // eslint-disable-next-line @typescript-eslint/ban-types
   return (target: object, decoratedPropertyName: string) => {
      // Get a reference to the Class of the target object which has been
      // decorated.
      const targetType: DecoratedHalObject<T> = target.constructor as DecoratedHalObject<T>;

      if (!Object.prototype.hasOwnProperty.call(targetType, '__embeddeds__')) {
         targetType.__embeddeds__ = {};
      }

      // Associate this property with the injectionKey provided in the
      // decorator call
      if (targetType.__embeddeds__) {
         (targetType.__embeddeds__ as Map<MetaData<T>>)[decoratedPropertyName] = {
            key: key,
            clazz: clazz
         };
      }
   };
};

/**
 * The link class decorator.  See [[HalIO]].
 *
 * @param {string} key The rel key such as "user:actions"
 * @param {Clazz<T> | undefined} clazz Pass Array here to return an array of LinkModels, String
 *        to return just the link's href, or leave undefined to return a single LinkModel
 * @returns {(target: object, decoratedPropertyName: (string | symbol)) => void}
 */
export const link = (key: string, clazz?: Clazz<String | Array<LinkModel>> | undefined) => {
   // eslint-disable-next-line @typescript-eslint/ban-types
   return (target: object, decoratedPropertyName: string) => {
      // Get a reference to the Class of the target object which has been
      // decorated.
      const targetType: DecoratedHalObject<any> = target.constructor as DecoratedHalObject<any>;

      if (!Object.prototype.hasOwnProperty.call(targetType, '__links__')) {
         targetType.__links__ = {};
      }

      // Associate this property with the injectionKey provided in the
      // decorator call
      if (targetType.__links__) {

         (targetType.__links__ as Map<MetaData<any>>)[decoratedPropertyName] = {
            key: key,
            clazz: (clazz != null) ? clazz : LinkModel
         };
      }
   };
};

/**
 * Serialize your data model to a JSON object.  When using [[HalIO]], this is done
 * internally for the developer.
 *
 * @param {object} Your decorated data model or a model that extends HalModel
 * @returns {object} Returns the JSON object
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export const serialize = (halModel: object, modelSchema: any): object => {
   return serializer.serialize(modelSchema, halModel);
};
/**
 * Deserialize a JSON object to your data model.  When using [[HalIO]], this is done
 * internally for the developer.
 *
 * @param {{new(...args: any[]): T}} modelSchema The data model class
 * @param {object} jsonObject The JSON object
 * @param {"halfred".Resource} resource Always leave this undefined, used internally
 * @returns {T} Returns your data model instance containing the data
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export const deserialize = <T extends object>(modelSchema: new(...args: any[]) => T, jsonObject: object, resource?: halfred.Resource | undefined): T => {
   const data: T = hasMobXSerializeDecorators(modelSchema) ? serializer.deserialize(modelSchema, jsonObject) : (jsonObject as T);

   validateOptionalMobXSerializable(modelSchema, data);

   if (resource == null) {
      resource = halfred.parse(jsonObject);
   }

   const decoratedHalSchema: DecoratedHalObject<T> = modelSchema as DecoratedHalObject<T>;

   addLinks(decoratedHalSchema, data, resource);
   validateOptionalLinks(decoratedHalSchema, data);

   addEmbeddeds(decoratedHalSchema, data, resource);
   validateOptionalEmbeddeds(decoratedHalSchema, data);

   return data;
};

const addEmbeddeds = <T>(modelClass: DecoratedHalObject<T>, modelInstance: T, resource: halfred.Resource) => {
   const embeddedInfo: Map<MetaData<any>> | undefined = modelClass.__embeddeds__;

   if (embeddedInfo) {
      for (const prop in embeddedInfo) {
         const metadata: MetaData<any> = embeddedInfo[prop];

         const resources = resource.embeddedResourceArray(metadata.key);

         if (resources) {

            (modelInstance as any)[prop] = resources.map((resource: any, index: number) => {
               try {

                  return deserialize(metadata.clazz, resource, resource);
               }
               catch (error) {

                  throw new Error(`Error deserializing embedded property = '${prop}', key = '${metadata.key}' of class '${(modelInstance as any).constructor.name}', element at index: ${index}\nResource='${JSON.stringify(
                     resource,
                     null,
                     4)}'\n` + error);
               }
            });
         }
      }
   }
};

const validateOptionalEmbeddeds = <T>(modelClass: DecoratedHalObject<T>, modelInstance: T) => {
   const embeddedInfo: Map<MetaData<any>> | undefined = modelClass.__embeddeds__;

   if (embeddedInfo) {
      for (const prop in embeddedInfo) {
         const isOptional = Reflect.getOwnMetadata(optionalMetadataKey, (modelInstance as any).constructor, prop);
         const value = (modelInstance as any)[prop];
         const valueExists = (value !== undefined && value !== null);

         if (!isOptional && !valueExists) {
            throw new Error(
               `A value does not exist for required field '${prop}' on class '${(modelInstance as any).constructor.name}'\n`+
               "NOTE: @optional requires the Model to have at least one @serializable"
            );
         }

         if (valueExists && value.constructor.__embeddeds__) {
            validateOptionalEmbeddeds(value.constructor, value);
         }
      }
   }
};

const convertToLinkModel = (link: halfred.Link): LinkModel => {
   const linkModel = new LinkModel(link.href, link.title, link.name, link.templated);

   linkModel.profile = link.profile;
   linkModel.deprecation = link.deprecation;
   linkModel.type = link.type;
   linkModel.hreflang = link.hreflang;

   return linkModel;
};

const convertToLinkModelArray = (linkArray: Array<halfred.Link>): Array<LinkModel> => {
   const linkModelArray = [];

   const len = linkArray.length;
   for (let i = 0; i < len; i++) {

      linkModelArray.push(convertToLinkModel(linkArray[i]));
   }

   return linkModelArray;
};

const addLinks = <T>(modelClass: DecoratedHalObject<T>, modelInstance: T, resource: halfred.Resource) => {
   const linkInfo: Map<MetaData<any>> | undefined = modelClass.__links__;

   if (linkInfo) {
      for (const prop in linkInfo) {
         const metadata: MetaData<any> = linkInfo[prop];
         const linkArray = resource.linkArray(metadata.key);

         if (linkArray && linkArray.length > 0) {
            const link = linkArray[0];

            if (metadata.clazz === String) {

               (modelInstance as any)[prop] = convertToLinkModel(link).getHref();
            }
            else if (metadata.clazz === Array) {

               (modelInstance as any)[prop] = convertToLinkModelArray(linkArray);
            }
            else {
               (modelInstance as any)[prop] = convertToLinkModel(link);
            }
         }
      }
   }
};

const validateOptionalLinks = <T>(modelClass: DecoratedHalObject<T>, modelInstance: T) => {

   const linkInfo: Map<MetaData<any>> | undefined = modelClass.__links__;

   if (linkInfo) {
      for (const prop in linkInfo) {
         const isOptional = Reflect.getOwnMetadata(optionalMetadataKey, (modelInstance as any).constructor, prop);
         const value = (modelInstance as any)[prop];
         const valueExists = (value !== undefined && value !== null);

         if (!isOptional && !valueExists) {
            throw new Error(
               `A value does not exist for required field '${prop}' on class '${(modelInstance as any).constructor.name}'\n`+
               "NOTE: @optional requires the Model to have at least one @serializable"
            );
         }

         if (valueExists && value.constructor.__links__) {
            validateOptionalLinks(value.constructor, value);
         }
      }
   }
};
